'use client';

/**
 * ObeyaPlanPositionHeaderCard — Plan Position's top-of-canvas panel:
 * a deterministic budget-vs-catalogue-floor bar, vendor concentration
 * bars, and the plan_commercial_position narrative underneath,
 * verbatim (the model's own text — this component never writes or
 * rewrites interpretive prose itself; it paints numbers and passes
 * the narrative through unedited).
 *
 * Self-fetching, own strategy (plan_commercial_position) — same
 * strategy obeya_plan_commercial_* used, unchanged. Pairs with
 * ObeyaPlanPositionRoomsCard (room_story) on the same tab; the two
 * stay separate model calls, only the layout merged.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — ObeyaPlanPositionHeaderCard
 * @see klyde_edge/klyde_intelligence/projects/plan_story.py
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';

interface PlanCommercialVendorRow { vendor: string; total: number; product_count: number; share_pct: number | null }
// One shape, both consumers — this mirrors PlanCommercialFacts's real
// nested structure (klyde_edge/klyde_intelligence/projects/plan_story.py)
// exactly, since the served response IS that model's own model_dump(),
// not a separately-flattened view built for this card.
// Every PatternBlock field is {pattern, data: {...}} — gap_analysis and
// vendor_portfolio both need the .data hop; context is a plain dict
// (not a PatternBlock), no .data on it. Verified against the real
// served payload, not assumed — this is the exact shape that crashed
// the card when .data was dropped from vendor_portfolio.
interface PlanPositionHeaderData {
  gap_analysis: {
    data: {
      approved_budget: number | null;
      plan_total_selected: number;
      plan_total_at_lowest_tier: number | null;
      plan_total_at_mid_tier: number | null;
      plan_total_at_premium_tier: number | null;
      open_requirement_count: number;
      // Computed in the BFF (plan_story._gap_chart_fields) — the card
      // paints these, it never derives them.
      to_lowest_tier: number | null;
      overspent_by: number | null;
      bar_spent_pct: number;
      bar_remaining_pct: number;
      bar_budget_pct: number | null;
      ladder_budget_pct: number | null;
      ladder_lowest_tier_pct: number | null;
      ladder_mid_tier_pct: number | null;
      ladder_premium_tier_pct: number | null;
    };
  };
  vendor_portfolio: {
    data: {
      vendors: PlanCommercialVendorRow[];
    };
  };
  context: {
    selected_requirement_count: number;
    requirement_count: number;
    project_committed_po_value: number | null;
  };
  narrative: string;
}

const money = (v: number) => `$${Math.round(v).toLocaleString()}`;
const VENDOR_COLORS = ['#7fa2ff', '#c3a3ff', '#5fd0a9', '#e0a94f', '#e58080'];
const TIER_COLORS = { lowest: '#e0a94f', mid: '#7fa2ff', premium: '#c3a3ff' };

export function ObeyaPlanPositionHeaderCard() {
  const { mcpId } = useViewportSelection();
  const { contentKey } = useCardIdentity();

  const strategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'plan_commercial_position'));
  const { data, isLoading } = useStrategyData<PlanPositionHeaderData>(
    strategy, { semantic_id: mcpId ?? '' }, !!mcpId && !!strategy,
  );
  const d = data?.data;

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_plan_position_header',
    status: (d ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: strategy ? ['strategy-data', strategy, { semantic_id: mcpId ?? '' }] : null,
    text: d ? `Plan Position: ${money(d.gap_analysis.data.plan_total_selected)} selected, ${d.gap_analysis.data.open_requirement_count} open` : 'Plan Position',
    json: d ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, d, mcpId, strategy]));

  if (!mcpId) return null;
  if (isLoading && !d) {
    return <div className={cn(text.muted, 'p-6 text-center')}>Generating…</div>;
  }
  if (!d) return null;

  const g = d.gap_analysis.data;
  const budget = g.approved_budget;
  const floor = g.plan_total_at_lowest_tier;
  const remaining = g.to_lowest_tier;
  const over = g.overspent_by;
  const spentPct = g.bar_spent_pct;
  const remPct = g.bar_remaining_pct;
  const budgetPct = g.bar_budget_pct;
  const tiers = [
    { key: 'lowest', label: 'lowest tier', total: g.plan_total_at_lowest_tier, pct: g.ladder_lowest_tier_pct, color: TIER_COLORS.lowest },
    { key: 'mid', label: 'mid tier', total: g.plan_total_at_mid_tier, pct: g.ladder_mid_tier_pct, color: TIER_COLORS.mid },
    { key: 'premium', label: 'premium tier', total: g.plan_total_at_premium_tier, pct: g.ladder_premium_tier_pct, color: TIER_COLORS.premium },
  ].filter((x) => x.total != null && x.pct != null);

  return (
    <div className="flex flex-col h-full p-6 gap-5 overflow-auto">
      {over != null && over > 0 && (
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-[28px] font-serif tabular-nums text-red-400">{money(over)} over</span>
          {floor != null && (
            <span className={cn(text.muted)}>catalogue floor for the whole plan {money(floor)}</span>
          )}
        </div>
      )}

      {/* Deterministic budget bar — spent, remaining-to-floor, budget line */}
      <div>
        <div className="relative h-3 rounded bg-muted/40 flex overflow-visible mb-6">
          <div className="h-full bg-red-400/80 rounded-l" style={{ width: `${spentPct}%` }} />
          {remaining != null && (
            <div
              className="h-full rounded-r"
              style={{
                width: `${remPct}%`,
                background: 'repeating-linear-gradient(135deg, rgba(224,169,79,.9) 0 5px, rgba(224,169,79,.22) 5px 10px)',
              }}
            />
          )}
          {budgetPct != null && (
            <div className="absolute -top-1 -bottom-1 w-[2px] bg-foreground" style={{ left: `${budgetPct}%` }}>
              <span className="absolute left-1.5 top-full mt-1 text-[10px] font-mono whitespace-nowrap text-foreground">
                budget {money(budget!)}
              </span>
            </div>
          )}
        </div>
        <div className={cn(text.muted, 'flex gap-5 flex-wrap text-[11px]')}>
          <span>selected, {d.context.selected_requirement_count} of {d.context.requirement_count} requirements <b className="text-foreground tabular-nums">{money(d.gap_analysis.data.plan_total_selected)}</b></span>
          {remaining != null && (
            <span>to reach the market floor for {d.gap_analysis.data.open_requirement_count} remaining <b className="text-foreground tabular-nums">{money(remaining)}</b></span>
          )}
          {d.context.project_committed_po_value != null && (
            <span>purchase orders raised <b className="text-foreground tabular-nums">{money(d.context.project_committed_po_value)}</b></span>
          )}
        </div>
      </div>

      {/* Whole plan at each benchmark tier vs budget — own scale (BFF ladder_*_pct) */}
      {tiers.length > 0 && (
        <div className="pt-4 border-t border-border">
          <div className={cn(text.eyebrow, 'mb-2')}>Whole plan at each benchmark tier</div>
          <div className="relative h-2 rounded bg-muted/40 mb-3">
            {g.ladder_budget_pct != null && (
              <div className="absolute -top-1 -bottom-1 w-[2px] bg-foreground" style={{ left: `${g.ladder_budget_pct}%` }} />
            )}
            {tiers.map((x) => (
              <div
                key={x.key}
                className="absolute top-1/2 w-[9px] h-[9px] rounded-full -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${x.pct}%`, background: x.color }}
              />
            ))}
          </div>
          <div className="flex gap-4 flex-wrap text-[11px]">
            {budget != null && (
              <span className={cn(text.muted)}>
                <span className="inline-block w-[2px] h-[9px] mr-1.5 bg-foreground align-middle" />
                budget <b className="text-foreground tabular-nums">{money(budget)}</b>
              </span>
            )}
            {tiers.map((x) => (
              <span key={x.key} className={cn(text.muted)}>
                <span className="inline-block w-[7px] h-[7px] rounded-full mr-1.5" style={{ background: x.color }} />
                {x.label} <b className="text-foreground tabular-nums">{money(x.total!)}</b>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Vendor concentration — selected lines only */}
      {d.vendor_portfolio.data.vendors.length > 0 && (
        <div className="pt-4 border-t border-border">
          <div className={cn(text.eyebrow, 'mb-2')}>Vendor concentration — selected lines</div>
          <div className="flex h-2.5 rounded overflow-hidden bg-muted/40 mb-2">
            {d.vendor_portfolio.data.vendors.map((v, i) => (
              <div key={v.vendor} style={{ width: `${v.share_pct ?? 0}%`, background: VENDOR_COLORS[i % VENDOR_COLORS.length] }} />
            ))}
          </div>
          <div className="flex gap-4 flex-wrap text-[11px]">
            {d.vendor_portfolio.data.vendors.map((v, i) => (
              <span key={v.vendor} className={cn(text.muted)}>
                <span
                  className="inline-block w-[7px] h-[7px] rounded-sm mr-1.5"
                  style={{ background: VENDOR_COLORS[i % VENDOR_COLORS.length] }}
                />
                {v.vendor} <b className="text-foreground tabular-nums">{v.share_pct ?? '—'}%</b> · {v.product_count} product{v.product_count !== 1 ? 's' : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* The model's own read — unedited */}
      {d.narrative && (
        <div className="pt-4 border-t border-border">
          <div className={cn(text.eyebrow, 'mb-2')}>plan_commercial_position · generated</div>
          <p className={cn(text.body, 'leading-relaxed')}>{d.narrative}</p>
        </div>
      )}
    </div>
  );
}
